import { useEffect, useState } from "react";
import { Plus, Sparkle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const TIPI = [["sconto_percentuale", "Sconto %"], ["sconto_fisso", "Sconto fisso €"],
  ["prezzo_fisso", "Prezzo promozionale"], ["omaggio", "Omaggio"], ["pacchetto", "Pacchetto"]];
const GIORNI = [["lun", "L"], ["mar", "M"], ["mer", "M"], ["gio", "G"], ["ven", "V"], ["sab", "S"], ["dom", "D"]];
const STATI = { bozza: ["Bozza", "bg-zinc-100 text-zinc-600 border-zinc-300"],
  attiva: ["Attiva", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  archiviata: ["Archiviata", "bg-amber-100 text-amber-800 border-amber-300"] };
const inputCls = "w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1";

const EMPTY = { name: "", descrizione: "", tipo_offerta: "sconto_percentuale", valore: "",
  prezzo_promozionale: "", condizioni: "", data_inizio: "", data_fine: "", giorni: [],
  fascia_da: "", fascia_a: "", servizi: [], coupon_id: "", stato: "bozza" };

export default function Promozioni() {
  const [items, setItems] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [modal, setModal] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiIdea, setAiIdea] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const load = () => {
    api.get("/promotions").then((r) => setItems(r.data)).catch(() => {});
    api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...modal,
      valore: modal.valore === "" ? null : parseFloat(modal.valore),
      prezzo_promozionale: modal.prezzo_promozionale === "" ? null : parseFloat(modal.prezzo_promozionale),
      coupon_id: modal.coupon_id || null, data_inizio: modal.data_inizio || null,
      data_fine: modal.data_fine || null };
    try {
      if (modal.id) await api.patch(`/promotions/${modal.id}`, payload);
      else await api.post("/promotions", payload);
      toast.success("Promozione salvata");
      setModal(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const aiAssist = async () => {
    setAiBusy(true);
    try {
      const r = await api.post("/promotions/ai-assist", { idea: aiIdea, tipo_offerta: modal.tipo_offerta });
      const s = r.data;
      setModal({ ...modal, name: s.titolo || modal.name, descrizione: s.descrizione || modal.descrizione,
        condizioni: s.condizioni || modal.condizioni });
      toast.success("Suggerimento AI applicato — rivedi e salva per confermare");
      setAiOpen(false);
    } catch (err) { toast.error(apiError(err)); }
    finally { setAiBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare definitivamente la promozione "${p.name}"?`)) return;
    try {
      await api.delete(`/promotions/${p.id}`);
      toast.success("Promozione eliminata");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggleGiorno = (g) => setModal({
    ...modal, giorni: modal.giorni.includes(g) ? modal.giorni.filter((x) => x !== g) : [...modal.giorni, g] });

  return (
    <div className="space-y-5 max-w-4xl" data-testid="promozioni-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Promozioni</h1>
          <p className="text-xs text-zinc-500">La promozione definisce l'offerta; il coupon è il voucher che dà diritto a usufruirne.</p>
        </div>
        <button onClick={() => setModal({ ...EMPTY })} data-testid="promo-new"
          className="h-11 px-4 rounded-md bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2">
          <Plus size={16} weight="bold" /> Nuova promozione
        </button>
      </div>

      {!items && <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>}
      {items?.length === 0 && (
        <div className="border border-dashed border-zinc-300 rounded-lg p-8 text-center text-sm text-zinc-400" data-testid="promo-empty">
          Nessuna promozione. Creane una manualmente o con l'assistente AI.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {items?.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-lg p-4 space-y-2" data-testid={`promo-card-${p.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-display font-bold text-zinc-900 truncate">{p.name}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATI[p.stato]?.[0] ? STATI[p.stato][1] : ""}`}>
                {STATI[p.stato]?.[0] || p.stato}
              </span>
            </div>
            <p className="text-xs text-zinc-500 line-clamp-2">{p.descrizione}</p>
            <div className="text-[11px] text-zinc-400">
              {TIPI.find(([k]) => k === p.tipo_offerta)?.[1]}
              {p.valore != null && ` · ${p.valore}`}{p.prezzo_promozionale != null && ` · €${p.prezzo_promozionale}`}
              {p.data_inizio && ` · ${p.data_inizio} → ${p.data_fine || "…"}`}
            </div>
            <div className="text-[11px] text-zinc-500">
              {p.coupon_name ? `Coupon: ${p.coupon_name} (${p.coupon_scaricati} scaricati)` : "Nessun coupon collegato"}
              {" · "}Prenotazioni generate: {p.prenotazioni_generate}
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setModal({ ...EMPTY, ...p, coupon_id: p.coupon_id || "",
                data_inizio: p.data_inizio || "", data_fine: p.data_fine || "",
                valore: p.valore ?? "", prezzo_promozionale: p.prezzo_promozionale ?? "" })}
                className="h-8 px-3 rounded-md border border-zinc-300 text-xs font-semibold" data-testid={`promo-edit-${p.id}`}>Modifica</button>
              <button onClick={() => remove(p)}
                className="h-8 px-3 rounded-md border border-rose-200 text-rose-600 text-xs font-semibold" data-testid={`promo-delete-${p.id}`}>Elimina</button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setModal(null)}>
          <form onSubmit={save} className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="promo-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">{modal.id ? "Modifica promozione" : "Nuova promozione"}</h3>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setAiOpen(true)} data-testid="promo-ai-open"
                  className="h-8 px-3 rounded-md bg-sky-50 border border-sky-200 text-sky-700 text-xs font-bold flex items-center gap-1">
                  <Sparkle size={13} weight="fill" /> Assistente AI
                </button>
                <button type="button" onClick={() => setModal(null)} className="p-2 text-zinc-500"><X size={18} /></button>
              </div>
            </div>
            <div>
              <label className={labelCls}>Nome offerta</label>
              <input required value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })}
                className={inputCls} data-testid="promo-name" />
            </div>
            <div>
              <label className={labelCls}>Descrizione</label>
              <textarea value={modal.descrizione} onChange={(e) => setModal({ ...modal, descrizione: e.target.value })}
                rows={2} className={`${inputCls} h-auto py-2`} data-testid="promo-descrizione" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Tipo offerta</label>
                <select value={modal.tipo_offerta} onChange={(e) => setModal({ ...modal, tipo_offerta: e.target.value })}
                  className={inputCls} data-testid="promo-tipo">
                  {TIPI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>{modal.tipo_offerta === "prezzo_fisso" ? "Prezzo promo (€)" : "Valore"}</label>
                {modal.tipo_offerta === "prezzo_fisso" ? (
                  <input type="number" min="0" step="0.5" value={modal.prezzo_promozionale}
                    onChange={(e) => setModal({ ...modal, prezzo_promozionale: e.target.value })}
                    className={inputCls} data-testid="promo-prezzo" />
                ) : (
                  <input type="number" min="0" step="0.5" value={modal.valore}
                    onChange={(e) => setModal({ ...modal, valore: e.target.value })}
                    className={inputCls} data-testid="promo-valore" />
                )}
              </div>
              <div>
                <label className={labelCls}>Valida dal</label>
                <input type="date" value={modal.data_inizio} onChange={(e) => setModal({ ...modal, data_inizio: e.target.value })}
                  className={inputCls} data-testid="promo-dal" />
              </div>
              <div>
                <label className={labelCls}>Valida fino al</label>
                <input type="date" value={modal.data_fine} onChange={(e) => setModal({ ...modal, data_fine: e.target.value })}
                  className={inputCls} data-testid="promo-al" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Giorni validi</label>
              <div className="flex gap-1.5">
                {GIORNI.map(([k, l]) => (
                  <button key={k} type="button" onClick={() => toggleGiorno(k)} data-testid={`promo-giorno-${k}`}
                    className={`w-8 h-8 rounded-md text-xs font-bold border ${modal.giorni.includes(k) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-500 border-zinc-300"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Fascia oraria da</label>
                <input type="time" value={modal.fascia_da} onChange={(e) => setModal({ ...modal, fascia_da: e.target.value })}
                  className={inputCls} data-testid="promo-fascia-da" />
              </div>
              <div>
                <label className={labelCls}>Fascia oraria a</label>
                <input type="time" value={modal.fascia_a} onChange={(e) => setModal({ ...modal, fascia_a: e.target.value })}
                  className={inputCls} data-testid="promo-fascia-a" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Coupon collegato (voucher per il cliente)</label>
              <select value={modal.coupon_id} onChange={(e) => setModal({ ...modal, coupon_id: e.target.value })}
                className={inputCls} data-testid="promo-coupon">
                <option value="">— Nessuno —</option>
                {coupons.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Condizioni</label>
              <textarea value={modal.condizioni} onChange={(e) => setModal({ ...modal, condizioni: e.target.value })}
                rows={2} className={`${inputCls} h-auto py-2`} data-testid="promo-condizioni" />
            </div>
            <div>
              <label className={labelCls}>Stato</label>
              <select value={modal.stato} onChange={(e) => setModal({ ...modal, stato: e.target.value })}
                className={inputCls} data-testid="promo-stato">
                {Object.keys(STATI).map((k) => <option key={k} value={k}>{STATI[k][0]}</option>)}
              </select>
            </div>
            <button className="w-full h-12 rounded-md bg-zinc-900 text-white text-sm font-semibold" data-testid="promo-save">
              Salva promozione
            </button>
          </form>
        </div>
      )}

      {aiOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setAiOpen(false)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-lg p-5 space-y-3"
            onClick={(e) => e.stopPropagation()} data-testid="promo-ai-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <Sparkle size={18} weight="fill" className="text-sky-500" /> Assistente AI
              </h3>
              <button onClick={() => setAiOpen(false)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <p className="text-xs text-zinc-500">Descrivi l'idea: l'AI propone titolo, descrizione e condizioni. <b>Nulla viene pubblicato</b>: rivedi e confermi tu con Salva.</p>
            <textarea value={aiIdea} onChange={(e) => setAiIdea(e.target.value)} rows={4}
              placeholder="Es. serata degustazione vini del sud a €35, solo il giovedì sera…"
              className={`${inputCls} h-auto py-2`} data-testid="promo-ai-idea" />
            <button onClick={aiAssist} disabled={aiBusy || !aiIdea.trim()}
              className="w-full h-11 rounded-md bg-zinc-900 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="promo-ai-generate">{aiBusy ? "Generazione in corso..." : "Genera suggerimento"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
